import { useCallback, useEffect, useMemo, useRef } from 'react';

import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import { createNativeEmbedChildClient } from '@atlassian/native-embeds-core/services/client/child/main';
import { maybeParseNativeEmbedParams } from '@atlassian/native-embeds-core/services/client/iframe-integration/main';
import {
	NativeEmbedCoreFeature,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types/features/main';
import type { NativeEmbedChildCommand } from '@atlassian/native-embeds-core/types/commands/types';

import { UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

/**
 * Initialises the native embed child client when a page is running inside a
 * native embed iframe (i.e. the URL contains `nativeEmbedChildId` and
 * `nativeEmbedParentId` query params set by the host). Gated behind the
 * `cc-maui-experiment` experiment.
 *
 * Should be called from embedded view pages, embedded edit pages, and
 * embedded live docs.
 *
 * @returns sendChildCommand — a stable callback to send a child command to the
 * native embed host, or a no-op if not running inside a native embed iframe.
 */
export const usePageNativeEmbedClient = (): {
	sendChildCommand: (command: NativeEmbedChildCommand) => void;
	triggerToggleFullScreen: () => void;
	isNativeEmbed: boolean;
} => {
	const clientRef = useRef<ReturnType<typeof createNativeEmbedChildClient> | null>(null);

	const nativeEmbedParams = useMemo(() => {
		const isNativeEmbedEnabled =
			UNSAFE_expValNoExposure('cc-maui-experiment', 'isEnabled', false) ||
			fg('platform_native_embeds_rollout_non_maui_experience');

		if (!isNativeEmbedEnabled || typeof window === 'undefined') {
			return null;
		}

		return maybeParseNativeEmbedParams(new URLSearchParams(window.location.search));
	}, []);
	const isNativeEmbed = !!nativeEmbedParams;

	useEffect(() => {
		if (!nativeEmbedParams) {
			return;
		}

		const bridge = getBrowserBridgeAPI();
		const { childClientId, parentClientId } = nativeEmbedParams;

		const supportedFeatures = new Set<NativeEmbedFeature>([NativeEmbedCoreFeature.NotifyHostClick]);

		const client = createNativeEmbedChildClient(
			bridge,
			childClientId,
			parentClientId,
			supportedFeatures,
		);

		clientRef.current = client;

		return () => {
			client.destroy();
			clientRef.current = null;
		};
	}, [nativeEmbedParams]);

	const sendChildCommand = useCallback((command: NativeEmbedChildCommand) => {
		clientRef.current?.sendChildCommand(command);
	}, []);

	const triggerToggleFullScreen = useCallback(() => {
		clientRef.current?.sendToggleFullScreen();
	}, []);

	return { sendChildCommand, triggerToggleFullScreen, isNativeEmbed };
};
